import type { ColumnOptions } from '@Types/Blocks/Columns';
import type { EditorOptions } from '@Types/Blocks/Editor';
import type { EventsOptions } from '@Types/Blocks/Events';
import type { FactFigureOptions } from '@Types/Blocks/FactFigure';
import type { FaqsOptions } from '@Types/Blocks/Faqs';
import type { FormOptions } from '@Types/Blocks/Form';
import type { HeroOptions } from '@Types/Blocks/Hero';
import type { ImageOptions } from '@Types/Blocks/Image';
import type { NewsOptions } from '@Types/Blocks/News';
import type { TeamsOptions } from '@/_types/Blocks/Teams';
import type { TxtImgOptions } from '@Types/Blocks/TxtImg';
import type { VideoOptions } from '@Types/Blocks/Video';
import type { BlockKey, FontStyleElement, Options } from '@Types/Shared/Options';

import { type ReactElement } from 'react';

import defu from 'defu';

import { defaultContext } from '@Shared/Context';

import type { Page404Options } from '@/_types/404/page404';
import type { SearchPageOptions } from '@/_types/Search/Search';
import type { SingleEventsPostOptions } from '@/_types/Shared/advanced-block-page/EventSingle';
import type {
	EventsListingOptions,
	FaqsListingOptions,
	NewsListingOptions,
	TeamsListingOptions,
} from '@/_types/Shared/advanced-block-page/Listing';
import type { SinglePostOptions } from '@/_types/Shared/advanced-block-page/PostSingle';
import type { CtasModel } from '@/_types/Shared/Button';
import type { BodyOptions, SectionDecorationOptions } from '@/_types/Shared/Common';
import type { FooterOptions } from '@/_types/Shared/Footer';
import type { HeaderOptions } from '@/_types/Shared/Header';
import type { Color } from '@/_types/Shared/PaletteEdit';
import type { WsiwygFontsOptions } from '@/App/Admin/components/common/WsiwygControls';
import type { SingleTeamsOptions } from '@/_types/Shared/advanced-block-page/TeamSingle';

export const WsiwygFontsFormatsStyle = (
	block: BlockKey,
	context: string,
	fontFormats: WsiwygFontsOptions
) => {
	const style = {} as any;

	Object.entries(fontFormats).forEach(([key, value]) => {
		const format = key as keyof WsiwygFontsOptions;
		const _value = value as any;
		style[`--m-${block}-${context}-${format}-font-style`] = _value.fontStyle;
		style[`--m-${block}-${context}-${format}-color`] = _value.color;
		style[`--m-${block}-${context}-${format}-font-size`] = _value.fontSize;
		style[`--m-${block}-${context}-${format}-font-family`] = _value.fontFamily;
		style[`--m-${block}-${context}-${format}-leading`] = _value.leading;
		style[`--m-${block}-${context}-${format}-font-weight`] = _value.fontWeight;
	});

	return style;
};

const defaultBrowserFontSize = 16;

export const globalColor = (options?: BodyOptions) => {
	return {
		'--m-color-global-bg': `var(--m-color-${options?.bgColor})`,
	};
};

export const circleColor = (options?: SectionDecorationOptions) => {
	return {
		'--m-color-decoration-bubble-left': `var(--m-color-${options?.decorationColorLeft})`,
		'--m-color-decoration-bubble-right': `var(--m-color-${options?.decorationColorRight})`,
	};
};

export const singleNewsStyle = (options?: SinglePostOptions) => {
	return {
		...getFontStyleCSS('single-news-heading-title', options?.singleHeadingTitleFontStyle),
		...getFontStyleCSS('single-news-info', options?.singleInfoFontStyle),
		...getFontStyleCSS('related-news-title', options?.relatedTitleFontStyle),

		'--m-single-news-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-single-news-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,
		'--m-related-news-container-border-color': `var(--m-color-${options?.relatedBorderColor})`,
		'--m-related-news-bg-color': `var(--m-color-${options?.relatedBgColor})`,
		'--m-related-news-secondary-color': `var(--m-color-${options?.relatedBgSecondaryColor})`,

		'--m-single-news-img-border-radius': options?.imgBordeRadius,
		'--m-related-news-container-border-radius': options?.relatedBordeRadius,
	};
};

export const listingNewsStyle = (options?: NewsListingOptions) => {
	return {
		...getFontStyleCSS('listing-news-heading-title', options?.listingHeadingTitleFontStyle),
		...getFontStyleCSS('listing-news-result', options?.listingResultFontStyle),
		...getFontStyleCSS('listing-news-filters', options?.listingFiltersFontStyle),
		...getFontStyleCSS('listing-news-pagination', options?.paginationNumberFontStyle),

		'--m-listing-news-filters-bg-color': `var(--m-color-${options?.listingFiltersBgColor})`,
		'--m-listing-news-filters-actif-color': `var(--m-color-${options?.listingFiltersActifColor})`,
		'--m-listing-news-filters-border-color': `var(--m-color-${options?.listingFiltersBorderColor})`,
		'--m-listing-news-pagination-color-hover': `var(--m-color-${options?.paginationNumberHoverColor})`,
		'--m-listing-news-pagination-bg-color': `var(--m-color-${options?.paginationBgColor})`,
		'--m-listing-news-pagination-bg-color-hover': `var(--m-color-${options?.paginationBgHoverColor})`,
		'--m-listing-news-pagination-arrow-color': `var(--m-color-${options?.paginationArrowColor})`,
		'--m-listing-news-pagination-arrow-color-hover': `var(--m-color-${options?.paginationArrowHoverColor})`,
		'--m-listing-news-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-listing-news-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,

		'--m-listing-news-filters-border-radius': options?.listingFiltersBorderRadius,
		'--m-listing-news-pagination-border-radius': options?.paginationBorderRadius,
		'--m-listing-news-container-border-radius': options?.borderRadius,

		...getFontStyleCSS('listing-news-reset-btn-label', options?.resetButtonStyle?.labelFontStyle),
		'--m-listing-news-reset-btn-label-color-hover': `var(--m-color-${options?.resetButtonStyle?.hoverLabelColor})`,
	};
};

export const singleEventsStyle = (options?: SingleEventsPostOptions) => {
	return {
		...getFontStyleCSS('single-events-heading-title', options?.singleHeadingTitleFontStyle),
		...getFontStyleCSS('single-events-info', options?.singleInfoFontStyle),
		...getFontStyleCSS('single-events-title-info', options?.singleTitleInfoFontStyle),
		...getFontStyleCSS('related-events-title', options?.relatedTitleFontStyle),

		'--m-single-events-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-single-events-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,
		'--m-single-events-info-border-radius': options?.eventsInfoContainerBorderRadius,
		'--m-single-events-info-border-color': `var(--m-color-${options?.eventsInfoContainerBorderColor})`,
		'--m-single-events-info-bg-color': `var(--m-color-${options?.eventsInfoContainerBgColor})`,
		'--m-single-events-icon-color': `var(--m-color-${options?.iconColor})`,
		'--m-related-events-container-border-color': `var(--m-color-${options?.relatedBorderColor})`,
		'--m-related-events-bg-color': `var(--m-color-${options?.relatedBgColor})`,
		'--m-related-events-secondary-color': `var(--m-color-${options?.relatedBgSecondaryColor})`,
		'--m-single-events-img-border-radius': options?.imgBordeRadius,
		'--m-related-events-container-border-radius': options?.relatedBordeRadius,
	};
};

export const listingEventsStyle = (options?: EventsListingOptions) => {
	return {
		...getFontStyleCSS('listing-events-heading-title', options?.listingHeadingTitleFontStyle),
		...getFontStyleCSS('listing-events-result', options?.listingResultFontStyle),
		...getFontStyleCSS('listing-events-filters', options?.listingFiltersFontStyle),
		...getFontStyleCSS('listing-events-pagination', options?.paginationNumberFontStyle),
		...getFontStyleCSS('listing-events-reset-btn-label', options?.resetButtonStyle?.labelFontStyle),
		...getFontStyleCSS(
			'listing-events-calendar-current-day-content',
			options?.calendar?.cardCurrentDayFontStyle
		),
		...getFontStyleCSS(
			'listing-events-calendar-current-day-cta-label',
			options?.calendar?.cardCurrentDayCtaDayFontStyle
		),
		...getFontStyleCSS('listing-events-calendar-card', options?.calendar?.cardEventsFontStyle),
		...getFontStyleCSS(
			'listing-events-calendar-card-day-cta-label',
			options?.calendar?.cardCtaDayFontStyle
		),
		...getFontStyleCSS(
			'listing-events-calendar-card-day-cta-label-without-event',
			options?.calendar?.cardDayFontStyle
		),
		...getFontStyleCSS(
			'listing-events-calendar-card-modal-title',
			options?.calendar?.cardModalTitleFontStyle
		),
		...getFontStyleCSS(
			'listing-events-calendar-card-modal-content',
			options?.calendar?.cardModalContentFontStyle
		),
		...getFontStyleCSS('listing-events-calendar-week-days', options?.calendar?.weekDaysFontStyle),

		'--m-listing-events-filters-bg-color': `var(--m-color-${options?.listingFiltersBgColor})`,
		'--m-listing-events-filters-actif-color': `var(--m-color-${options?.listingFiltersActifColor})`,
		'--m-listing-events-filters-border-color': `var(--m-color-${options?.listingFiltersBorderColor})`,
		'--m-listing-events-pagination-color-hover': `var(--m-color-${options?.paginationNumberHoverColor})`,
		'--m-listing-events-pagination-bg-color': `var(--m-color-${options?.paginationBgColor})`,
		'--m-listing-events-pagination-bg-color-hover': `var(--m-color-${options?.paginationBgHoverColor})`,
		'--m-listing-events-pagination-arrow-color': `var(--m-color-${options?.paginationArrowColor})`,
		'--m-listing-events-pagination-arrow-color-hover': `var(--m-color-${options?.paginationArrowHoverColor})`,
		'--m-listing-events-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-listing-events-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,
		'--m-listing-events-reset-btn-label-color-hover': `var(--m-color-${options?.resetButtonStyle?.hoverLabelColor})`,
		'--m-listing-events-calendar-current-day-bg-color': `var(--m-color-${options?.calendar?.cardCurrentDayBgColor})`,
		'--m-listing-events-calendar-current-day-border-color': `var(--m-color-${options?.calendar?.cardCurrentDayBorderColor})`,
		'--m-listing-events-calendar-current-day-cta-bg-color': `var(--m-color-${options?.calendar?.cardCurrentDayCtaDayBgColor})`,
		'--m-listing-events-calendar-cta-day-bg-color': `var(--m-color-${options?.calendar?.cardCtaDayBgColor})`,
		'--m-listing-events-calendar-modal-bg-color': `var(--m-color-${options?.calendar?.cardModalBgColor})`,
		'--m-listing-events-calendar-card-day-bg-color': `var(--m-color-${options?.calendar?.cardDayBgColor})`,
		'--m-listing-events-calendar-card-with-event-bg-color': `var(--m-color-${options?.calendar?.cardWithEventBgColor})`,
		'--m-listing-events-calendar-card-without-event-bg-color': `var(--m-color-${options?.calendar?.cardWithoutEventBgColor})`,

		'--m-listing-events-filters-border-radius': options?.listingFiltersBorderRadius,
		'--m-listing-events-pagination-border-radius': options?.paginationBorderRadius,
		'--m-listing-events-container-border-radius': options?.borderRadius,
		'--m-listing-events-calendar-card-current-border-radius': options?.calendar?.cardBorderRadius,
		'--m-listing-events-calendar-card-day-border-radius': options?.calendar?.cardCtaDayBorderRadius,
		'--m-listing-events-calendar-modal-border-radius': options?.calendar?.cardModalBorderRadius,
		'--m-listing-events-calendar-card-without-event-day-cta-border-radius':
			options?.calendar?.cardWithoutEventCtaDayBorderRadius,
		'--m-listing-events-calendar-card-without-event-border-radius':
			options?.calendar?.cardWithoutEventBorderRadius,
		'--m-listing-events-calendar-card-with-event-border-radius':
			options?.calendar?.cardWithEventBorderRadius,
	};
};

export const listingFaqsStyle = (options?: FaqsListingOptions) => {
	return {
		...getFontStyleCSS('listing-faqs-heading-title', options?.listingHeadingTitleFontStyle),
		...getFontStyleCSS('listing-faqs-result', options?.listingResultFontStyle),
		...getFontStyleCSS('listing-faqs-filters', options?.listingFiltersFontStyle),
		...getFontStyleCSS('listing-faqs-pagination', options?.paginationNumberFontStyle),
		...getFontStyleCSS('listing-faqs-reset-btn-label', options?.resetButtonStyle?.labelFontStyle),

		'--m-listing-faqs-filters-bg-color': `var(--m-color-${options?.listingFiltersBgColor})`,
		'--m-listing-faqs-filters-actif-color': `var(--m-color-${options?.listingFiltersActifColor})`,
		'--m-listing-faqs-filters-border-color': `var(--m-color-${options?.listingFiltersBorderColor})`,
		'--m-listing-faqs-pagination-color-hover': `var(--m-color-${options?.paginationNumberHoverColor})`,
		'--m-listing-faqs-pagination-bg-color': `var(--m-color-${options?.paginationBgColor})`,
		'--m-listing-faqs-pagination-bg-color-hover': `var(--m-color-${options?.paginationBgHoverColor})`,
		'--m-listing-faqs-pagination-arrow-color': `var(--m-color-${options?.paginationArrowColor})`,
		'--m-listing-faqs-pagination-arrow-color-hover': `var(--m-color-${options?.paginationArrowHoverColor})`,
		'--m-listing-faqs-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-listing-faqs-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,
		'--m-listing-faqs-reset-btn-label-color-hover': `var(--m-color-${options?.resetButtonStyle?.hoverLabelColor})`,

		'--m-listing-faqs-filters-border-radius': options?.listingFiltersBorderRadius,
		'--m-listing-faqs-pagination-border-radius': options?.paginationBorderRadius,
		'--m-listing-faqs-container-border-radius': options?.borderRadius,
	};
};

export const singleTeamsStyle = (options?: SingleTeamsOptions) => {
	return {
		...getFontStyleCSS('single-teams-heading-title', options?.singleHeadingTitleFontStyle),

		'--m-single-teams-bg-color': `var(--m-color-${options?.bgColor})`,
	};
};

export const listingTeamsStyle = (options?: TeamsListingOptions) => {
	return {
		...getFontStyleCSS('listing-teams-heading-title', options?.listingHeadingTitleFontStyle),
		...getFontStyleCSS('listing-teams-result', options?.listingResultFontStyle),
		...getFontStyleCSS('listing-teams-filters', options?.listingFiltersFontStyle),
		...getFontStyleCSS('listing-teams-pagination', options?.paginationNumberFontStyle),
		...getFontStyleCSS('listing-teams-reset-btn-label', options?.resetButtonStyle?.labelFontStyle),

		'--m-listing-teams-filters-bg-color': `var(--m-color-${options?.listingFiltersBgColor})`,
		'--m-listing-teams-filters-actif-color': `var(--m-color-${options?.listingFiltersActifColor})`,
		'--m-listing-teams-filters-border-color': `var(--m-color-${options?.listingFiltersBorderColor})`,
		'--m-listing-teams-pagination-color-hover': `var(--m-color-${options?.paginationNumberHoverColor})`,
		'--m-listing-teams-pagination-bg-color': `var(--m-color-${options?.paginationBgColor})`,
		'--m-listing-teams-pagination-bg-color-hover': `var(--m-color-${options?.paginationBgHoverColor})`,
		'--m-listing-teams-pagination-arrow-color': `var(--m-color-${options?.paginationArrowColor})`,
		'--m-listing-teams-pagination-arrow-color-hover': `var(--m-color-${options?.paginationArrowHoverColor})`,
		'--m-listing-teams-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-listing-teams-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,
		'--m-listing-teams-reset-btn-label-color-hover': `var(--m-color-${options?.resetButtonStyle?.hoverLabelColor})`,
	
		'--m-listing-teams-filters-border-radius': options?.listingFiltersBorderRadius,
		'--m-listing-teams-pagination-border-radius': options?.paginationBorderRadius,
		'--m-listing-teams-container-border-radius': options?.borderRadius,
	};
};

export const page404Style = (options?: Page404Options) => {
	return {
		...getFontStyleCSS('404-title', options?.titleFontStyle),
		...getFontStyleCSS('404-description', options?.descriptionFontStyle),

		'--m-404-container-border-radius': options?.containerBorderRadius,

		'--m-404-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-404-bg-container-color': `var(--m-color-${options?.containerBgColor})`,

		'--m-404-bg-color': `var(--m-color-${options?.bgColor})`,

		'--m-404-btn-label-color-hover': `var(--m-color-${options?.btnStyle?.hoverLabelColor})`,
		'--m-404-btn-bg-color': `var(--m-color-${options?.btnStyle?.bgColor})`,
		'--m-404-btn-bg-color-hover': `var(--m-color-${options?.btnStyle?.hoverBgColor})`,
		'--m-404-btn-border-color-hover': `var(--m-color-${options?.btnStyle?.hoverBorderColor})`,
		'--m-404-btn-border-color': `var(--m-color-${options?.btnStyle?.borderColor})`,
		'--m-404-btn-radius': options?.btnStyle?.borderRadius,
		...getFontStyleCSS('404-btn-label', options?.btnStyle?.labelFontStyle),
	};
};

export const pageSearchStyle = (options?: SearchPageOptions) => {
	return {
		...getFontStyleCSS('search-heading-title', options?.listingHeadingTitleFontStyle),
		...getFontStyleCSS('search-result', options?.listingResultFontStyle),
		...getFontStyleCSS('search-filters', options?.listingFiltersFontStyle),
		...getFontStyleCSS('search-pagination', options?.paginationNumberFontStyle),

		'--m-search-filters-bg-color': `var(--m-color-${options?.listingFiltersBgColor})`,
		'--m-search-filters-actif-color': `var(--m-color-${options?.listingFiltersActifColor})`,
		'--m-search-filters-border-color': `var(--m-color-${options?.listingFiltersBorderColor})`,
		'--m-search-pagination-color-hover': `var(--m-color-${options?.paginationNumberHoverColor})`,
		'--m-search-pagination-bg-color': `var(--m-color-${options?.paginationBgColor})`,
		'--m-search-pagination-bg-color-hover': `var(--m-color-${options?.paginationBgHoverColor})`,
		'--m-search-pagination-arrow-color': `var(--m-color-${options?.paginationArrowColor})`,
		'--m-search-pagination-arrow-color-hover': `var(--m-color-${options?.paginationArrowHoverColor})`,
		'--m-search-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-search-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,

		'--m-search-filters-border-radius': options?.listingFiltersBorderRadius,
		'--m-search-pagination-border-radius': options?.paginationBorderRadius,
		'--m-search-container-border-radius': options?.borderRadius,

		...getFontStyleCSS('search-reset-btn-label', options?.resetButtonStyle?.labelFontStyle),
		'--m-search-reset-btn-label-color-hover': `var(--m-color-${options?.resetButtonStyle?.hoverLabelColor})`,

		...getFontStyleCSS('search-card-title', options?.cardTilte),
		...getFontStyleCSS('search-card-excerpt', options?.cardExcerpt),
		...getFontStyleCSS('search-card-infos', options?.cardInfos),
		...getFontStyleCSS('search-card-readMore', options?.cardReadMoreFontStyle),

		'--m-search-card-border-radius': options?.cardBorderRadius,
		'--m-search-card-image-border-radius': options?.cardImageBorderRadius,
		'--m-search-card-bg-color': `var(--m-color-${options?.cardBgColor})`,
		'--m-search-card-border-color': `var(--m-color-${options?.cardBorderColor})`,
		'--m-search-card-underline-color': `var(--m-color-${options?.cardUnderlineColor})`,
		'--m-search-card-readMore-color-hover': `var(--m-color-${options?.cardReadMoreColorHover})`,
	};
};

export const globalColorsStyles = (options: Color[]) => {
	const colorStyles: any = {};

	options.forEach((color) => {
		colorStyles['--m-colors-' + color.slug] = 'rgb(' + color.color + ')';
	});

	return colorStyles;
};

const getColorsCSSVar = (colors: Color[]) => {
	const colorStyles: any = {};

	colors.forEach((color) => {
		colorStyles['--m-color-' + color.slug] = 'rgb(' + color.color + ')';
	});

	return colorStyles;
};

const getFontsCSSVar = (fonts: FontStyleElement[]) => {
	const fontStyles: any = {};

	fonts.forEach((font) => {
		fontStyles['--m-font-style-' + font.id] = `${
			font.fontWeight != 'regular' ? font.fontWeight : ''
		} ${font.fontSize / defaultBrowserFontSize}rem/${font.lineHeight} ${font.fontFamily}`;
		fontStyles['--m-font-color-' + font.id] = `var(--m-color-${font.color})`;
		fontStyles['--m-font-decoration-' + font.id] = font.textDecoration;
		fontStyles['--m-font-case-' + font.id] = font.textCase;
		fontStyles['--m-font-italic-' + font.id] = font.italic ? 'italic' : 'normal';
		fontStyles['--m-font-letter-spacing-' + font.id] = `${font.letterSpacing}rem`;
	});

	return fontStyles;
};

export const getFontStyleCSS = (prefix: string, fontStyle: string | undefined) => {
	return {
		['--m-' + prefix + '-font-style']: `var(--m-font-style-${fontStyle})`,
		['--m-' + prefix + '-color']: `var(--m-font-color-${fontStyle})`,
		['--m-' + prefix + '-font-decoration']: `var(--m-font-decoration-${fontStyle})`,
		['--m-' + prefix + '-font-case']: `var(--m-font-case-${fontStyle})`,
		['--m-' + prefix + '-font-italic']: `var(--m-font-italic-${fontStyle})`,
		['--m-' + prefix + '-font-letter-spacing']: `var(--m-font-letter-spacing-${fontStyle})`,
	};
};

export const getCTAStyles = (ctas: CtasModel, context: string) => {
	return {
		['--m-' + context + '-btn-primary-label-color-hover']:
			`var(--m-color-${ctas.primary?.hoverLabelColor})`,
		['--m-' + context + '-btn-primary-bg-color']: `var(--m-color-${ctas.primary?.bgColor})`,
		['--m-' + context + '-btn-primary-bg-color-hover']:
			`var(--m-color-${ctas.primary?.hoverBgColor})`,
		['--m-' + context + '-btn-primary-border-color-hover']:
			`var(--m-color-${ctas.primary?.hoverBorderColor})`,
		['--m-' + context + '-btn-primary-border-color']: `var(--m-color-${ctas.primary?.borderColor})`,
		['--m-' + context + '-btn-primary-radius']: ctas.primary?.borderRadius,
		['--m-' + context + '-btn-primary-border-width']: ctas.primary?.borderWidth,
		...getFontStyleCSS('' + context + '-btn-primary-label', ctas.primary?.labelFontStyle),

		['--m-' + context + '-btn-secondary-bg-color']: `var(--m-color-${ctas.secondary?.bgColor})`,
		['--m-' + context + '-btn-secondary-bg-color-hover']:
			`var(--m-color-${ctas.secondary?.hoverBgColor})`,
		['--m-' + context + '-btn-secondary-border-color']:
			`var(--m-color-${ctas.secondary?.borderColor})`,
		['--m-' + context + '-btn-secondary-border-color-hover']:
			`var(--m-color-${ctas.secondary?.hoverBorderColor})`,
		['--m-' + context + '-btn-secondary-label-color-hover']:
			`var(--m-color-${ctas.secondary?.hoverLabelColor})`,
		['--m-' + context + '-btn-secondary-radius']: ctas.secondary?.borderRadius,
		['--m-' + context + '-btn-secondary-border-width']: ctas.secondary?.borderWidth,
		...getFontStyleCSS('' + context + '-btn-secondary-label', ctas.secondary?.labelFontStyle),

		['--m-' + context + '-btn-tertiary-label-color-hover']:
			`var(--m-color-${ctas.tertiary?.hoverLabelColor})`,
		...getFontStyleCSS('' + context + '-btn-tertiary-label', ctas.tertiary?.labelFontStyle),

		['--m-' + context + '-btn-a-label-color-hover']: `var(--m-color-${ctas.a?.hoverLabelColor})`,
		...getFontStyleCSS('' + context + '-btn-a-label', ctas.a?.labelFontStyle),
	};
};

export const getContentFormat = (context: string, options: WsiwygFontsOptions | undefined) => {
	if (options) {
		return {
			...getFontStyleCSS(context + '-p', options ? options.p : ''),
			...getFontStyleCSS(context + '-h1', options ? options.h1 : ''),
			...getFontStyleCSS(context + '-h2', options ? options.h2 : ''),
			...getFontStyleCSS(context + '-h3', options ? options.h3 : ''),
			...getFontStyleCSS(context + '-h4', options ? options.h4 : ''),
			...getFontStyleCSS(context + '-h5', options ? options.h5 : ''),
			...getFontStyleCSS(context + '-h6', options ? options.h6 : ''),
		};
	} else {
		return {};
	}
};

export const FactFigureStyle = (options?: FactFigureOptions) => {
	return {
		...getFontStyleCSS('factFigure-fact', options?.factFontStyle),
		...getFontStyleCSS('factFigure-figure', options?.figureFontStyle),
		...getFontStyleCSS('factFigure-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('factFigure-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('factFigure-inside-title', options?.insideTitleFontStyle),

		...getContentFormat('factFigure-content', options?.contentFontFormats),

		'--m-factFigure-fact-wrapper-border-radius': options?.factWrapperBorderRadius,
		'--m-factFigure-container-border-radius': options?.containerBorderRadius,
		'--m-factFigure-wrapper-border-radius': options?.wrapperBorderRadius,

		'--m-factFigure-fact-wrapper-bg-color': `var(--m-color-${options?.factWrapperBgColor})`,
		'--m-factFigure-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-factFigure-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-factFigure-bg-wrapper-color': `var(--m-color-${options?.wrapperBgColor})`,
		'--m-factFigure-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-factFigure-list-color': `var(--m-color-${options?.listColor})`,

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'factFigure')),
	};
};

export const ColumnsStyle = (options?: ColumnOptions) => {
	return {
		...getFontStyleCSS('column-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('column-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('column-inside-title', options?.insideTitleFontStyle),

		...getContentFormat('column-content', options?.contentFontFormats),

		'--m-column-img-border-radius': options?.imgBorderRadius, // /!\ imgBorderRadius vs borderRadius
		'--m-column-container-border-radius': options?.containerBorderRadius,
		'--m-column-card-border-radius': options?.cardBorderRadius,

		'--m-column-img-opacity-color': `var(--m-color-${options?.imgOpacityColor})`,
		'--m-column-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-column-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-column-card-border-color': `var(--m-color-${options?.cardBorderColor})`,
		'--m-column-bg-card-color': `var(--m-color-${options?.cardBgColor})`,
		'--m-column-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-column-list-color': `var(--m-color-${options?.listColor})`,

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'column')),
	};
};

export const HeroStyle = (options?: HeroOptions) => {
	return {
		...getFontStyleCSS('hero-title', options?.titleFontStyle),
		...getFontStyleCSS('hero-text', options?.textFontStyle),

		'--m-hero-img-border-radius': options?.imgBorderRadius, // /!\ imgBorderRadius vs borderRadius
		'--m-hero-container-border-radius': options?.containerBorderRadius,

		'--m-hero-img-opacity-color': `var(--m-color-${options?.imgOpacityColor})`,
		'--m-hero-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-hero-bg-secondary-color': `var(--m-color-${options?.bgSecondaryColor})`,
		'--m-hero-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-hero-container-border-color': `var(--m-color-${options?.containerBorderColor})`,

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'hero')),
	};
};

export const VideoStyle = (options?: VideoOptions) => {
	return {
		...getFontStyleCSS('video-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('video-heading-description', options?.headingDescriptionFontStyle),

		'--m-video-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-video-container-border-radius': options?.containerBorderRadius,
		'--m-video-bg-container-color': `var(--m-color-${options?.containerBgColor})`,

		'--m-video-border-radius': options?.borderRadius,

		'--m-video-bg-color': `var(--m-color-${options?.bgColor})`,

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'video')),
	};
};

export const EditorStyle = (options?: EditorOptions) => {
	return {
		...getFontStyleCSS('editor-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('editor-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('editor-thead', options?.theadFontStyle),
		...getFontStyleCSS('editor-quote', options?.quoteFontStyle),
		...getFontStyleCSS('editor-table', options?.tableTextFontStyle),

		...getFontStyleCSS('editor-legende-img', options?.legendeImgFontStyle),

		...getContentFormat('editor-content', options?.contentFontFormats),

		'--m-editor-quote-border-radius': options?.quoteBorderRadius,
		'--m-editor-wrapper-border-radius': options?.wrapperBorderRadius,
		'--m-editor-table-border-radius': options?.tableBorderRadius,
		'--m-editor-container-border-radius': options?.containerBorderRadius,
		'--m-editor-img-border-radius': options?.imgBorderRadius,

		'--m-editor-quote-border-color': `var(--m-color-${options?.quoteBorderColor})`,
		'--m-editor-quote-bg-color': `var(--m-color-${options?.quoteBgColor})`,
		'--m-editor-quote-element-color': `var(--m-color-${options?.quoteElementColor})`,
		'--m-editor-list-color': `var(--m-color-${options?.listColor})`,
		'--m-editor-table-secondary-color': `var(--m-color-${options?.tableSecondaryColor})`,
		'--m-editor-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-editor-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-editor-wrapper-border-color': `var(--m-color-${options?.wrapperBorderColor})`,
		'--m-editor-bg-wrapper-color': `var(--m-color-${options?.wrapperBgColor})`,
		'--m-editor-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-editor-thead-bg-color': `var(--m-color-${options?.theadBgColor})`,

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'editor')),
	};
};

export const ImageStyle = (options?: ImageOptions) => {
	return {
		...getFontStyleCSS('img-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('img-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('img-inside-title', options?.insideTitleFontStyle),

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'img')),

		'--m-img-img-border-radius': options?.borderRadius,
		'--m-img-container-border-radius': options?.containerBorderRadius,

		'--m-img-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-img-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-img-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-img-bg-legend-color': `var(--m-color-${options?.bgLegendColor})`,
		'--m-img-list-color': `var(--m-color-${options?.listColor})`,

		...getContentFormat('img-content', options?.contentFontFormats),
	};
};

export const TeamsStyle = (options?: TeamsOptions) => {
	return {
		...getFontStyleCSS('teams-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('teams-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('teams-inside-title', options?.insideTitleFontStyle),
		...getFontStyleCSS('teams-inside-content', options?.insideDescriptionFontStyle),
		...getFontStyleCSS('teams-infos', options?.infoFontStyle),
		...getFontStyleCSS('teams-function', options?.functionFontStyle),

		'--m-teams-img-border-radius': options?.borderRadius,
		'--m-teams-container-border-radius': options?.containerBorderRadius,
		'--m-teams-wrapper-border-radius': options?.wrapperBorderRadius,

		'--m-teams-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-teams-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-teams-social-color': `var(--m-color-${options?.socialColor})`,
		'--m-teams-bg-wrapper-color': `var(--m-color-${options?.wrapperBgColor})`,
		'--m-teams-bg-color': `var(--m-color-${options?.bgColor})`,

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'teams')),
	};
};

export const TxtImgStyle = (options?: TxtImgOptions) => {
	return {
		...getFontStyleCSS('txtImg-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('txtImg-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('txtImg-inside-title', options?.insideTitleFontStyle),

		'--m-txtImg-img-border-radius': options?.imgBorderRadius, // /!\ imgBorderRadius vs borderRadius
		'--m-txtImg-wrapper-border-radius': options?.wrapperBorderRadius,
		'--m-txtImg-container-border-radius': options?.containerBorderRadius,
		'--m-txtImg-list-color': `var(--m-color-${options?.listColor})`,

		'--m-txtImg-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-txtImg-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-txtImg-bg-wrapper-color': `var(--m-color-${options?.wrapperBgColor})`,
		'--m-txtImg-bg-color': `var(--m-color-${options?.bgColor})`,

		...getContentFormat('txtImg-content', options?.contentFontFormats),

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'txtImg')),
	};
};

export const FaqsStyle = (options?: FaqsOptions) => {
	return {
		...getFontStyleCSS('faqs-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('faqs-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('faqs-inside-title', options?.insideTitleFontStyle),
		...getContentFormat('faqs-content', options?.contentFontFormats),

		'--m-faqs-wrapper-border-radius': options?.wrapperBorderRadius,
		'--m-faqs-container-border-radius': options?.containerBorderRadius,

		'--m-faqs-bg-wrapper-color': `var(--m-color-${options?.wrapperBgColor})`,
		'--m-faqs-wrapper-border-color': `var(--m-color-${options?.wrapperBorderColor})`,
		'--m-faqs-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-faqs-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-faqs-separation-color': `var(--m-color-${options?.separationColor})`,
		'--m-faqs-bg-color': `var(--m-color-${options?.bgColor})`,

		'--m-faqs-btn-archivelink-label-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverLabelColor})`,
		'--m-faqs-btn-archivelink-bg-color': `var(--m-color-${options?.archiveLinkStyles?.bgColor})`,
		'--m-faqs-btn-archivelink-bg-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverBgColor})`,
		'--m-faqs-btn-archivelink-border-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverBorderColor})`,
		'--m-faqs-btn-archivelink-border-color': `var(--m-color-${options?.archiveLinkStyles?.borderColor})`,
		'--m-faqs-btn-archivelink-radius': options?.archiveLinkStyles?.borderRadius,
		'--m-faqs-btn-archivelink-border-width': options?.archiveLinkStyles?.borderWidth,

		...getFontStyleCSS('faqs-btn-archivelink-label', options?.archiveLinkStyles?.labelFontStyle),

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'faqs')),
	};
};

export const NewsStyle = (options?: NewsOptions) => {
	return {
		...getFontStyleCSS('news-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('news-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('news-inside-title', options?.insideTitleFontStyle),
		...getFontStyleCSS('news-inside-description', options?.insideDescriptionFontStyle),
		...getFontStyleCSS('news-post-info', options?.postInfoFontStyle),
		...getFontStyleCSS('news-readMore', options?.readMoreFontStyle),

		'--m-news-img-border-radius': options?.imgBorderRadius, // /!\ imgBorderRadius vs borderRadius
		'--m-news-container-border-radius': options?.containerBorderRadius,
		'--m-news-card-border-radius': options?.cardBorderRadius,
		'--m-news-tag-border-radius': options?.tagBorderRadius,

		'--m-news-img-opacity-color': `var(--m-color-${options?.imgOpacityColor})`,
		'--m-news-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-news-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-news-card-border-color': `var(--m-color-${options?.cardBorderColor})`,
		'--m-news-bg-card-color': `var(--m-color-${options?.cardBgColor})`,
		'--m-news-bg-tag-color': `var(--m-color-${options?.tagBgColor})`,
		'--m-news-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-news-readMore-color-hover': `var(--m-color-${options?.readMoreColorHover})`,

		'--m-news-btn-archivelink-label-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverLabelColor})`,
		'--m-news-btn-archivelink-bg-color': `var(--m-color-${options?.archiveLinkStyles?.bgColor})`,
		'--m-news-btn-archivelink-bg-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverBgColor})`,
		'--m-news-btn-archivelink-border-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverBorderColor})`,
		'--m-news-btn-archivelink-border-color': `var(--m-color-${options?.archiveLinkStyles?.borderColor})`,
		'--m-news-btn-archivelink-radius': options?.archiveLinkStyles?.borderRadius,
		'--m-news-btn-archivelink-border-width': options?.archiveLinkStyles?.borderWidth,

		...getFontStyleCSS('news-btn-archivelink-label', options?.archiveLinkStyles?.labelFontStyle),

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'news')),
	};
};

export const EventsStyle = (options?: EventsOptions) => {
	return {
		...getFontStyleCSS('events-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('events-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('events-inside-title', options?.insideTitleFontStyle),
		...getFontStyleCSS('events-inside-description', options?.insideDescriptionFontStyle),
		...getFontStyleCSS('events-readMore', options?.readMoreFontStyle),
		...getFontStyleCSS('events-date', options?.dateFontStyle),
		...getFontStyleCSS('events-info', options?.infoFontStyle),

		'--m-events-img-border-radius': options?.imgBorderRadius, // /!\ imgBorderRadius vs borderRadius
		'--m-events-container-border-radius': options?.containerBorderRadius,
		'--m-events-card-border-radius': options?.cardBorderRadius,
		'--m-events-date-wrapper-border-radius': options?.dateWrapperBorderRadius,

		'--m-events-img-opacity-color': `var(--m-color-${options?.imgOpacityColor})`,
		'--m-events-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-events-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-events-card-border-color': `var(--m-color-${options?.cardBorderColor})`,
		'--m-events-bg-card-color': `var(--m-color-${options?.cardBgColor})`,
		'--m-events-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-events-icon-color': `var(--m-color-${options?.iconColor})`,
		'--m-events-date-wrapper-bg-color': `var(--m-color-${options?.dateWrapperBgColor})`,
		'--m-events-readMore-color-hover': `var(--m-color-${options?.readMoreColorHover})`,

		'--m-events-btn-archivelink-label-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverLabelColor})`,
		'--m-events-btn-archivelink-bg-color': `var(--m-color-${options?.archiveLinkStyles?.bgColor})`,
		'--m-events-btn-archivelink-bg-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverBgColor})`,
		'--m-events-btn-archivelink-border-color-hover': `var(--m-color-${options?.archiveLinkStyles?.hoverBorderColor})`,
		'--m-events-btn-archivelink-border-color': `var(--m-color-${options?.archiveLinkStyles?.borderColor})`,
		'--m-events-btn-archivelink-radius': options?.archiveLinkStyles?.borderRadius,
		'--m-events-btn-archivelink-border-width': options?.archiveLinkStyles?.borderWidth,
		...getFontStyleCSS('events-btn-archivelink-label', options?.archiveLinkStyles?.labelFontStyle),

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'events')),
	};
};

export const HeaderStyle = (options?: HeaderOptions) => {
	return {
		...getFontStyleCSS('header-mainnav-menuItems', options?.mainNav.types.menuItems),
		...getFontStyleCSS('header-topnav-menuItems', options?.topNav.types.menuItems),
		...getFontStyleCSS('header-mainnav-childMenuItems', options?.mainNav.types.menuChildItems),
		...getFontStyleCSS('header-topnav-childMenuItems', options?.topNav.types.menuChildItems),
		...getFontStyleCSS('header-mainnav-search', options?.mainNav.types.search),
		...getFontStyleCSS('header-topnav-search', options?.topNav.types.search),

		'--m-header-mainNav-bg': `var(--m-color-${options?.mainNav.types.bgNav})`,
		'--m-header-topNav-bg': `var(--m-color-${options?.topNav.types.bgNav})`,
		'--m-header-mainNav-menuchild-bg': `var(--m-color-${options?.mainNav.types.bgMenuChildItems})`,
		'--m-header-topNav-menuchild-bg': `var(--m-color-${options?.topNav.types.bgMenuChildItems})`,
		'--m-header-mainNav-menuchild-radius': options?.mainNav.types.radiusMenuChildItems,
		'--m-header-topNav-menuchild-radius': options?.topNav.types.radiusMenuChildItems,
		'--m-header-mainNav-search-bg': `var(--m-color-${options?.mainNav.types.bgSearch})`,
		'--m-header-topNav-search-bg': `var(--m-color-${options?.topNav.types.bgSearch})`,
		'--m-header-mainNav-search-radius': options?.mainNav.types.radiusSearch,
		'--m-header-topNav-search-radius': options?.topNav.types.radiusSearch,
		'--m-header-mainNav-icon-color': `var(--m-color-${options?.mainNav.types.colorIcons})`,
		'--m-header-topNav-icon-color': `var(--m-color-${options?.topNav.types.colorIcons})`,
		'--m-header-separation-color': `var(--m-color-${options?.separationColor})`,
		'--m-header-mainNav-logo-size-width': options?.mainNav.types.logoSizeWidth,
		'--m-header-mainNav-logo-size-height': options?.mainNav.types.logoSizeHeight,
		'--m-header-topNav-logo-size-width': options?.topNav.types.logoSizeWidth,
		'--m-header-topNav-logo-size-height': options?.topNav.types.logoSizeHeight,
		'--m-header-nav-radius': options?.radiusNav,

		'--m-header-topNav-cta-label-color-hover': `var(--m-color-${options?.topNav.types.cta?.hoverLabelColor})`,
		'--m-header-topNav-cta-bg-color': `var(--m-color-${options?.topNav.types.cta?.bgColor})`,
		'--m-header-topNav-cta-bg-color-hover': `var(--m-color-${options?.topNav.types.cta?.hoverBgColor})`,
		'--m-header-topNav-cta-border-color-hover': `var(--m-color-${options?.topNav.types.cta?.hoverBorderColor})`,
		'--m-header-topNav-cta-border-color': `var(--m-color-${options?.topNav.types.cta?.borderColor})`,
		'--m-header-topNav-cta-radius': options?.topNav.types.cta?.borderRadius,
		...getFontStyleCSS('header-topNav-cta-label', options?.topNav.types.cta?.labelFontStyle),

		'--m-header-mainNav-cta-label-color-hover': `var(--m-color-${options?.mainNav.types.cta?.hoverLabelColor})`,
		'--m-header-mainNav-cta-bg-color': `var(--m-color-${options?.mainNav.types.cta?.bgColor})`,
		'--m-header-mainNav-cta-bg-color-hover': `var(--m-color-${options?.mainNav.types.cta?.hoverBgColor})`,
		'--m-header-mainNav-cta-border-color-hover': `var(--m-color-${options?.mainNav.types.cta?.hoverBorderColor})`,
		'--m-header-mainNav-cta-border-color': `var(--m-color-${options?.mainNav.types.cta?.borderColor})`,
		'--m-header-mainNav-cta-radius': options?.mainNav.types.cta?.borderRadius,
		...getFontStyleCSS('header-mainNav-cta-label', options?.mainNav.types.cta?.labelFontStyle),
	};
};

export const FooterStyle = (options?: FooterOptions) => {
	return {
		...getFontStyleCSS('footer-titre', options?.fontStyles.titre),
		...getFontStyleCSS('footer-text', options?.fontStyles.text),
		...getFontStyleCSS('footer-bottom', options?.fontStyles.bottom),
		'--m-footer-colors-bg': `var(--m-color-${options?.colors.bg})`,
		'--m-footer-colors-bottomBg': `var(--m-color-${options?.colors.bottomBg})`,
		'--m-footer-colors-icon': `var(--m-color-${options?.colors.icon})`,
		'--m-footer-logo-size-width': options?.logoSizeWidth,
		'--m-footer-logo-size-height': options?.logoSizeHeight,
	};
};

export const FormStyle = (options?: FormOptions) => {
	return {
		...getFontStyleCSS('form-heading-title', options?.headingTitleFontStyle),
		...getFontStyleCSS('form-heading-description', options?.headingDescriptionFontStyle),
		...getFontStyleCSS('form-content', options?.contentFontStyle),

		'--m-form-input-color': `var(--m-color-${options?.inputTextColor})`,
		'--m-form-input-bg-color': `var(--m-color-${options?.inputBgColor})`,
		'--m-form-input-border-color': `var(--m-color-${options?.inputBorderColor})`,
		'--m-form-input-error-color': `var(--m-color-${options?.errorColor})`,
		'--m-form-input-border-radius': options?.inputBorderRadius,

		'--m-form-require-color': `var(--m-color-${options?.requireElementColor})`,
		'--m-form-bg-color': `var(--m-color-${options?.bgColor})`,
		'--m-form-bg-container-color': `var(--m-color-${options?.containerBgColor})`,
		'--m-form-container-border-color': `var(--m-color-${options?.containerBorderColor})`,
		'--m-form-wrapper-border-color': `var(--m-color-${options?.wrapperBorderColor})`,
		'--m-form-bg-wrapper-color': `var(--m-color-${options?.wrapperBgColor})`,
		'--m-form-secondary-color': `var(--m-color-${options?.secondaryColor})`,

		'--m-form-container-border-radius': options?.containerBorderRadius,
		'--m-form-wrapper-border-radius': options?.wrapperBorderRadius,

		...(options?.ctasStyle && getCTAStyles(options?.ctasStyle, 'form')),
		'--m-form-submit-label-color-hover': `var(--m-color-${options?.btnSubmitStyle?.hoverLabelColor})`,
		'--m-form-submit-bg-color': `var(--m-color-${options?.btnSubmitStyle?.bgColor})`,
		'--m-form-submit-bg-color-hover': `var(--m-color-${options?.btnSubmitStyle?.hoverBgColor})`,
		'--m-form-submit-border-color': `var(--m-color-${options?.btnSubmitStyle?.borderColor})`,
		'--m-form-submit-border-color-hover': `var(--m-color-${options?.btnSubmitStyle?.hoverBorderColor})`,
		'--m-form-submit-radius': options?.btnSubmitStyle?.borderRadius,
		...getFontStyleCSS('form-submit-label', options?.btnSubmitStyle?.labelFontStyle),
	};
};

export default function GlobalStyles({
	options: _options,
	children,
}: {
	options: Options;
	children: ReactElement;
}) {
	const options = defu(_options, defaultContext);

	let styles = {};

	const colors = getColorsCSSVar(options.colors.palettes);
	const fonts = getFontsCSSVar(options.fonts.fontStyles);

	styles = {
		...colors,
		...fonts,
		...FactFigureStyle(options.blocks.factFigure.default),
		...ColumnsStyle(options.blocks.column.default),
		...HeroStyle(options.blocks.hero.default),
		...VideoStyle(options.blocks.video.default),
		...EditorStyle(options.blocks.editor.default),
		...ImageStyle(options.blocks.image.default),
		...TeamsStyle(options.blocks.teams.default),
		...TxtImgStyle(options.blocks.txtImg.default),
		...FaqsStyle(options.blocks.faqs.default),
		...NewsStyle(options.blocks.news.default),
		...EventsStyle(options.blocks.events.default),
		...FormStyle(options.blocks.form.default),
		...HeaderStyle(options.layout.header),
		...FooterStyle(options.layout.footer),
		...singleNewsStyle(_options.layout.news.single),
		...listingNewsStyle(_options.layout.news.index),
		...singleEventsStyle(_options.layout.events.single),
		...listingEventsStyle(_options.layout.events.index),
		...listingFaqsStyle(_options.layout.faqs.index),
		...listingTeamsStyle(_options.layout.teams.index),
		...singleTeamsStyle(_options.layout.teams.single),
		...page404Style(_options.layout.page404),
		...pageSearchStyle(_options.layout.search),
		...globalColor(_options.layout.body),
		...circleColor(_options.layout.sectionDecoration),
	};

	return (
		<>
			<div data-style={true} style={styles}>
				{children}
			</div>
		</>
	);
}
